/* src/App.js */
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useLocation, useNavigate } from 'react-router-dom';
import { apiService } from './services/api';
import { getRatingColor, setIndividualTiers } from './utils/ratingColors';
import ErrorBoundary, { clearChunkReloadSentinel } from './components/ErrorBoundary';
import './App.css';

// Keep the import factory so a route's chunk can be fetched before React renders
// it. Without this the whole app waits on /api/current-user/ before it even asks
// for the code for the page you are on, turning one round trip into two.
const lazyRoute = (factory) => {
  // Clearing the reload sentinel on chunk SUCCESS (not on App mount) is what
  // makes the ErrorBoundary's one-reload guard real: App always mounts from the
  // main bundle before a failing chunk rejects, so clearing there re-armed the
  // auto-reload every time and a persistently missing chunk reloaded forever.
  const wrapped = () => factory().then((m) => { clearChunkReloadSentinel(); return m; });
  const Component = React.lazy(wrapped);
  Component.preload = wrapped;
  return Component;
};

const Home = lazyRoute(() => import('./pages/Home'));
const UserDetail = lazyRoute(() => import('./pages/UserDetail'));
const Teams = lazyRoute(() => import('./pages/Teams'));
const RatingGraph = lazyRoute(() => import('./pages/RatingGraph'));
const H2H = lazyRoute(() => import('./pages/H2H'));
const TeamDetail = lazyRoute(() => import('./pages/TeamDetail'));
const Login = lazyRoute(() => import('./pages/Login'));
const Register = lazyRoute(() => import('./pages/Register'));
const RecordGame = lazyRoute(() => import('./pages/RecordGame'));
const TeamBonding = lazyRoute(() => import('./pages/TeamBonding'));

// Longest prefix wins, so /teams/12 resolves to TeamDetail rather than Teams.
const ROUTE_CHUNKS = [
  ['/user/', UserDetail],
  ['/teams/', TeamDetail],
  ['/teams', Teams],
  ['/team-bonding', TeamBonding],
  ['/rating-graph', RatingGraph],
  ['/h2h', H2H],
  ['/login', Login],
  ['/register', Register],
  ['/record-game', RecordGame],
  ['/', Home],
];

const preloadForPath = (pathname) => {
  const match = ROUTE_CHUNKS.find(([prefix]) => pathname.startsWith(prefix));
  if (match) match[1].preload();
};

// chart.js + date-fns live in a ~270 KB chunk shared by the profile, team and
// analytics pages. Warm it while the browser is idle so the first click on any
// of them is instant instead of stalling on a blocking download.
const prefetchChartPages = () => {
  const run = () => { UserDetail.preload(); RatingGraph.preload(); };
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(run, { timeout: 3000 });
  } else {
    setTimeout(run, 1500);
  }
};

const NavTab = ({ to, label, isActive }) => (
  <Link to={to} className={`nav-btn-sci-fi ${isActive ? 'active' : ''}`}>
    {label}
  </Link>
);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Start the current page's chunk download immediately, in parallel with the
    // auth request, instead of after it.
    preloadForPath(window.location.pathname);
    checkAuth();
    prefetchChartPages();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await apiService.getCurrentUser();
      // Tier cutoffs are dynamic (recomputed from the active pool server-side);
      // feed them to the shared color/tier helpers before any page renders.
      if (response.data.tier_thresholds) setIndividualTiers(response.data.tier_thresholds);
      if (response.data.is_authenticated) setUser(response.data);
    } catch (error) {
      console.error('System: Auth Check Failed', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await apiService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      navigate('/');
    }
  };

  if (loading) return <div className="loading-screen">INITIALIZING SYSTEM...</div>;

  return (
    <div className="scifi-interface">
      {/* HEADER */}
      <header className="top-hud">
        <Link to="/" className="logo-section" style={{ textDecoration: 'none', color: 'inherit' }}>
          PINGPONG<span className="highlight">FORCES</span>
        </Link>
        
        <div className="user-section">
          {user ? (
             <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
               <span className="data-value" style={{color: getRatingColor(user.elo)}}>
                 {user.username} [{Math.round(user.elo)}]
               </span>
               <button onClick={handleLogout} className="btn btn-primary" style={{padding: '0.25rem 0.75rem', fontSize: '0.8rem'}}>
                 LOGOUT
               </button>
             </div>
          ) : (
            <Link to="/login" className="btn btn-primary" style={{padding: '0.5rem 1rem', textDecoration: 'none'}}>
              LOGIN
            </Link>
          )}
        </div>
      </header>

      {/* CONTENT DECK */}
      <main className="main-deck">
        <nav className="nav-bar">
          <NavTab to="/" label="LEADERBOARD" isActive={location.pathname === '/'} />
          <NavTab to="/teams" label="TEAMS" isActive={location.pathname === '/teams'} />
          <NavTab to="/team-bonding" label="BONDING" isActive={location.pathname === '/team-bonding'} />
          <NavTab to="/rating-graph" label="ANALYTICS" isActive={location.pathname === '/rating-graph'} />
          <NavTab to="/h2h" label="HEAD-TO-HEAD" isActive={location.pathname === '/h2h'} />
          {user && (
            <NavTab to="/record-game" label="+ RECORD GAME" isActive={location.pathname === '/record-game'} />
          )}
        </nav>

        <div className="viewport-content">
          <ErrorBoundary key={location.pathname}>
          <React.Suspense fallback={<div className="loading-screen">LOADING...</div>}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/user/:username" element={<UserDetail currentUser={user} />} />
              <Route path="/teams" element={<Teams />} />
              <Route path="/teams/:teamId" element={<TeamDetail />} />
              <Route path="/rating-graph" element={<RatingGraph />} />
              <Route path="/seasons" element={<Navigate to="/" />} />
              <Route path="/h2h" element={<H2H />} />
              <Route path="/login" element={user ? <Navigate to="/" /> : <Login onLogin={setUser} />} />
              <Route path="/register" element={user ? <Navigate to="/" /> : <Register onLogin={setUser} />} />
              <Route path="/register/:token" element={user ? <Navigate to="/" /> : <Register onLogin={setUser} />} />
              <Route path="/record-game" element={<RecordGame user={user} onGameRecorded={checkAuth} />} />
              <Route path="/team-bonding" element={<TeamBonding />} />
            </Routes>
          </React.Suspense>
          </ErrorBoundary>
        </div>
      </main>
    </div>
  );
}

const AppWrapper = () => <Router><App /></Router>;

export default AppWrapper;
